import { formatTimecode } from '../../../utils/timecode';
import { MaTelopConfig, applyDelimiterRule, resolveMaTelopTrackColor } from './maTelopConfig';
import { MaSegment, effectiveClipDurationSeconds, stripExtension } from './maTypes';
import { resolveMaDisplayName } from './parseMaDisplayNames';

/**
 * MA セグメントのクリップ配置から、動画に焼き込むテロップ（ASS 字幕）を生成する。
 *
 * MA 自動化スクリプト（ma_automation.py の _generate_ass_subtitles）の移植:
 * - 1 クリップ = 1 テロップ。開始位置に「タイムスタンプ メディア名 [トラック] ゲイン」を表示
 * - 背景ボックス（BgBox: BorderStyle=3、トラック色）と文字（TextOutline: 黒縁取り）の
 *   2 レイヤーを同じ位置に重ねて描く（showBackground = false は文字レイヤーのみ = 背景なし）
 * - 同時に表示されるテロップは重ならないようスロット（行）を割り当て、
 *   左上 / 右上 / 左下 / 右下の基準端から積み上げる
 * - 寸法は 1080p 基準の設定値を実動画サイズ（PlayResY / 1080）でスケールする
 *
 * 出力は純粋な文字列で、ffmpeg の `subtitles` フィルタにそのまま渡せる。
 * libass はシステムフォントを参照できないため、フォントは `fontsdir` で同梱し
 * `Fontname` / `\fn` に OTF 内部名（maTelopFonts.ts の assFontName）を書く。
 */

/** テロップ 1 件（セグメント相対秒・表示テキスト・背景色） */
export interface MaTelopEntry {
  startSeconds: number;
  endSeconds: number;
  text: string;
  /** 背景色 `#RRGGBB` */
  bgColor: string;
}

interface BuildMaTelopEntriesOptions {
  fps: number;
  config: MaTelopConfig;
  /**
   * 表示名 CSV のマージ結果（sequence.displayNames）。一致したクリップは
   * メディア名の代わりに表示名を出す（`_` 区切り短縮は表示名にも適用する）
   */
  displayNames?: Map<string, string>;
}

/** ゲイン表示（+3dB / -6.5dB） */
function formatGainLabel(db: number): string {
  // 負数も絶対値で四捨五入する（Math.round(-32.5) = -32 を避ける）
  const rounded = (Math.sign(db) * Math.round(Math.abs(db) * 10)) / 10;
  return `${rounded > 0 ? '+' : ''}${rounded}dB`;
}

/** テロップ 1 件のテキスト組み立てに必要なクリップ情報 */
export interface MaTelopTextSource {
  /** セグメント相対の開始秒 */
  startSeconds: number;
  /** メディア名（拡張子なし） */
  mediaName: string;
  trackName: string | null;
  track: number;
  gainDb: number;
}

/**
 * 設定の表示内容フラグに従ってテロップ文字列を組み立てる
 * （「タイムスタンプ メディア名 [トラック] ゲイン」）。
 * 焼き込み（buildMaTelopEntries）と設定画面の見本表示で共用する。
 * 表示する要素が無ければ空文字
 */
export function formatMaTelopText(
  source: MaTelopTextSource,
  config: MaTelopConfig,
  fps: number,
): string {
  const start = Math.max(0, source.startSeconds);
  const parts: string[] = [];
  if (config.showTimestamp) {
    parts.push(
      config.timestampFormat === 'frames'
        ? `${Math.round(start * fps)}F`
        : formatTimecode(start, { fps, withFrames: true }),
    );
  }
  if (config.showMediaName) parts.push(source.mediaName);
  if (config.showTrackName) {
    const trackLabel = source.trackName ?? (source.track > 0 ? `Tr.${source.track}` : null);
    if (trackLabel) parts.push(`[${trackLabel}]`);
  }
  if (config.showGain && source.gainDb !== 0) parts.push(formatGainLabel(source.gainDb));
  return parts.join(' ');
}

/**
 * セグメントのクリップをテロップ一覧に変換する。
 * 表示時間はクリップの実効長（トリム・後着優先カット反映）または固定秒。
 * 実尺不明（duration 未解析）のクリップは固定秒で表示する。
 * メディア名は表示名 CSV（displayNames）に一致すれば表示名を優先し、
 * どちらの名前にも `_` 区切り短縮（applyDelimiterRule）を適用する
 */
export function buildMaTelopEntries(
  segment: MaSegment,
  { fps, config, displayNames }: BuildMaTelopEntriesOptions,
): MaTelopEntry[] {
  const entries: MaTelopEntry[] = [];
  const sorted = [...segment.clips].sort(
    (a, b) => a.startSeconds - b.startSeconds || a.line - b.line,
  );

  sorted.forEach(clip => {
    const start = Math.max(0, clip.startSeconds);
    // 実尺不明なら固定秒を実尺の代わりに使う（後着優先カットの短縮は反映される）
    const effective = effectiveClipDurationSeconds(clip, config.fixedDurationSeconds);
    if (effective <= 0) return; // 同時開始カットで全消しされたクリップ
    const duration =
      config.durationMode === 'fixed' ? Math.max(0.1, config.fixedDurationSeconds) : effective;

    const displayName = displayNames ? resolveMaDisplayName(displayNames, clip) : null;
    const text = formatMaTelopText(
      {
        startSeconds: start,
        mediaName: applyDelimiterRule(
          displayName ?? stripExtension(clip.audioItem.file_name),
          config,
        ),
        trackName: clip.trackName,
        track: clip.track,
        gainDb: clip.gainDb,
      },
      config,
      fps,
    );
    if (!text) return;

    entries.push({
      startSeconds: start,
      endSeconds: start + duration,
      text,
      bgColor: resolveMaTelopTrackColor(config, clip.trackName, clip.track),
    });
  });

  return entries;
}

// ===== ASS 生成 =====

/** `#RRGGBB` → ASS の `&HAABBGGRR`（alpha 00 = 不透明） */
export function hexToAssColor(hex: string, alpha = '00'): string {
  const m = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/.exec(hex);
  if (!m) return `&H${alpha}FFFFFF`;
  return `&H${alpha}${m[3]}${m[2]}${m[1]}`.toUpperCase();
}

/** 秒 → ASS 時刻 `H:MM:SS.CC`（センチ秒は切り捨て） */
export function secondsToAssTime(seconds: number): string {
  const total = Math.max(0, seconds);
  const centis = Math.floor(total * 100 + 1e-6);
  const h = Math.floor(centis / 360000);
  const m = Math.floor((centis % 360000) / 6000);
  const s = Math.floor((centis % 6000) / 100);
  const cc = centis % 100;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(cc).padStart(2, '0')}`;
}

/**
 * ASS テキストのエスケープ。
 * - NFC 正規化: macOS 由来のファイル名は濁点・半濁点が分解された NFD
 *   （「デ」=「テ」+ 結合濁点 U+3099）で、libass は結合文字を合成せず
 *   フォントに単独グリフが無いためトーフになる。合成済み（NFC）に揃える
 * - `{` `}` はオーバーライドブロックの開始 / 終了、`\` は制御シーケンスの先頭として
 *   解釈されるため全角に置き換える（libass には安全なエスケープ構文がない）
 * - 改行は 1 行表示のため空白にする
 */
export function escapeAssText(text: string): string {
  return text
    .normalize('NFC')
    .replace(/\\/g, '＼')
    .replace(/\{/g, '｛')
    .replace(/\}/g, '｝')
    .replace(/[\r\n]+/g, ' ');
}

/**
 * 同時に表示されるテロップへ重ならない行（スロット）を割り当てる。
 * 期限切れのスロットは解放し、最小の空き番号を使う（開始時刻昇順前提）
 */
export function assignTelopSlots(entries: MaTelopEntry[]): number[] {
  const active: Array<{ end: number; slot: number }> = [];
  return entries.map(entry => {
    const alive = active.filter(a => a.end > entry.startSeconds);
    active.length = 0;
    active.push(...alive);
    const used = new Set(alive.map(a => a.slot));
    let slot = 0;
    while (used.has(slot)) slot += 1;
    active.push({ end: entry.endSeconds, slot });
    return slot;
  });
}

export interface BuildMaTelopAssOptions {
  config: MaTelopConfig;
  /** 動画の実サイズ（ASS の PlayRes。寸法スケールの基準） */
  playResX: number;
  playResY: number;
  /** ASS に書くフォント名（maTelopFonts.ts の assFontName） */
  fontName: string;
}

const POSITION_FLAGS: Record<
  MaTelopConfig['position'],
  { alignment: number; right: boolean; bottom: boolean }
> = {
  'top-left': { alignment: 7, right: false, bottom: false },
  'top-right': { alignment: 9, right: true, bottom: false },
  'bottom-left': { alignment: 1, right: false, bottom: true },
  'bottom-right': { alignment: 3, right: true, bottom: true },
};

/** 1080p 基準の BgBox の Outline（= 背景ボックスのパディング） */
const BGBOX_OUTLINE_BASE = 4;

/**
 * テロップ一覧から ASS 字幕ファイルの内容を生成する。
 * entries は buildMaTelopEntries の戻り値（開始時刻昇順）を想定
 */
export function buildMaTelopAss(entries: MaTelopEntry[], options: BuildMaTelopAssOptions): string {
  const { config, playResX, playResY, fontName } = options;
  const scale = playResY > 0 ? playResY / 1080 : 1;
  const px = (base: number) => Math.max(0, Math.round(base * scale));

  const fontSize = Math.max(1, px(config.fontSize));
  const marginL = px(config.marginX);
  const marginV = px(config.marginY);
  const bgboxOutline = Math.max(1, px(BGBOX_OUTLINE_BASE));
  const textOutlineWidth = config.textOutline ? Math.max(1, px(config.outlineWidth)) : 0;
  const { alignment, right, bottom } = POSITION_FLAGS[config.position];

  const primary = hexToAssColor(config.fontColor);
  const black = '&H00000000';
  const transparentText = '&HFF000000';

  const header = [
    '[Script Info]',
    'Title: Soremo MA Time Chart',
    'ScriptType: v4.00+',
    `PlayResX: ${playResX}`,
    `PlayResY: ${playResY}`,
    'WrapStyle: 2',
    'ScaledBorderAndShadow: yes',
    'Timer: 100.0000',
    '',
    '[V4+ Styles]',
    'Format: Name, Fontname, Fontsize, PrimaryColour, SecondaryColour, OutlineColour, BackColour, Bold, Italic, Underline, StrikeOut, ScaleX, ScaleY, Spacing, Angle, BorderStyle, Outline, Shadow, Alignment, MarginL, MarginR, MarginV, Encoding',
    // 背景ボックス（文字は透明、BorderStyle=3 で Outline 分のパディング付きの箱を描く）
    `Style: BgBox,${fontName},${fontSize},${transparentText},&H000000FF,${black},${black},0,0,0,0,100,100,0,0,3,${bgboxOutline},0,${alignment},${marginL},${marginL},${marginV},1`,
    // 文字 + 縁取り（縁取りなしは Outline 0）
    `Style: Text,${fontName},${fontSize},${primary},&H000000FF,${black},${black},0,0,0,0,100,100,0,0,1,${textOutlineWidth},0,${alignment},${marginL},${marginL},${marginV},1`,
    '',
    '[Events]',
    'Format: Layer, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text',
  ];

  // 行の高さ（ボックス + 余白）と、BgBox と Text の Outline 差分による位置補正
  const lineHeight = fontSize + 2 * bgboxOutline + px(4);
  const borderOffset = bgboxOutline - textOutlineWidth;
  const slots = assignTelopSlots(entries);

  const events: string[] = [];
  entries.forEach((entry, i) => {
    const start = secondsToAssTime(entry.startSeconds);
    const end = secondsToAssTime(entry.endSeconds);
    const text = escapeAssText(entry.text);
    const slot = slots[i];

    const xAnchor = right ? playResX - marginL : marginL;
    const yAnchor = bottom ? playResY - marginV - slot * lineHeight : marginV + slot * lineHeight;
    const textX = xAnchor + (right ? -borderOffset : borderOffset);
    const textY = yAnchor + (bottom ? -borderOffset : borderOffset);

    // 背景ボックスは showBackground 時のみ（無色 = 文字 + 縁取りだけ描く）
    if (config.showBackground) {
      const bgColor = hexToAssColor(entry.bgColor);
      events.push(
        `Dialogue: 0,${start},${end},BgBox,,0,0,0,,{\\pos(${xAnchor},${yAnchor})\\fn${fontName}\\3c${bgColor}&}${text}`,
      );
    }
    events.push(
      `Dialogue: 1,${start},${end},Text,,0,0,0,,{\\pos(${textX},${textY})\\fn${fontName}\\3c${black}&}${text}`,
    );
  });

  return [...header, ...events].join('\n') + '\n';
}

/** セグメントから直接 ASS を生成する（buildMaTelopEntries + buildMaTelopAss） */
export function buildMaTelopAssForSegment(
  segment: MaSegment,
  options: BuildMaTelopAssOptions & { fps: number; displayNames?: Map<string, string> },
): string {
  const entries = buildMaTelopEntries(segment, {
    fps: options.fps,
    config: options.config,
    displayNames: options.displayNames,
  });
  return buildMaTelopAss(entries, options);
}
